// The Peak Lists section of global search: whether it shows at all, what it
// asks for, where a row links to, and what its second line says. Kept out of
// Search.vue so the rules are unit-tested rather than living inline in the
// template.

import { peakBaggingWebVisible } from '../peak_bagging/webVisibility'

/** Rows the section shows. A doorway to the catalogue, not a second one. */
export const PEAK_LIST_RESULT_LIMIT = 3

export interface GeoPoint {
  lat: number
  lng: number
}

export interface SearchPeakListVariables {
  query?: string
  mineOnly: boolean
  lat?: number
  lon?: number
  page: number
  perPage: number
}

/**
 * The question the section asks, typed or not.
 *
 * With nothing typed it asks for the lists near the walker — the server ranks
 * nearest first whenever a point is given — so search opened in Dublin leads
 * with the Dublin and Wicklow rounds. Once something is typed the point is
 * dropped: with a point the server only returns lists that have a peak inside
 * the radius, and "munros" typed in Dublin has to find the Munros.
 *
 * Without a usable point the empty box falls back to the curated catalogue
 * order. The backend rejects half a coordinate, so a point is sent whole or
 * not at all. Same rule as the apps' searchPeakListRequest.
 */
export function searchPeakListVariables (query: string, geo: GeoPoint | null | undefined): SearchPeakListVariables {
  const trimmed = (query || '').trim()
  const variables: SearchPeakListVariables = { mineOnly: false, page: 1, perPage: PEAK_LIST_RESULT_LIMIT }

  if (trimmed) {
    variables.query = trimmed
  } else if (geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng)) {
    variables.lat = geo.lat
    variables.lon = geo.lng
  }

  return variables
}

export interface SearchPeakList {
  id: string
  slug: string
  name: string
  locationLabel?: string | null
  webPath?: string | null
  peaksCount: number
  baggedCount?: number | null
  baggedBadgeUrl?: string | null
  nonBaggedBadgeUrl?: string | null
}

/**
 * Where a row links to: the list's canonical page, or the legacy address for a
 * list with no country yet — the same fallback the apps share a list at.
 */
export function peakListHref (list: Pick<SearchPeakList, 'slug' | 'webPath'>): string {
  return list.webPath || `/peaks/lists/${encodeURIComponent(list.slug)}`
}

/**
 * The row's second line: where the round is, and how far through it the
 * walker is — progress only once something has been bagged, since "0/282" on
 * every untouched row is noise. Matches the apps' peakListSubtitle.
 */
export function peakListSubtitle (list: Pick<SearchPeakList, 'locationLabel' | 'baggedCount' | 'peaksCount'>): string | null {
  const place = (list.locationLabel || '').trim()
  const bagged = list.baggedCount || 0
  if (bagged <= 0) return place || null

  const progress = `${bagged}/${list.peaksCount}`
  return place ? `${place} · ${progress}` : progress
}

/**
 * The questions asked with nothing typed, in priority order:
 *
 * 1. The walker's own lists — mine-only with no point is ordered
 *    recently-bagged-first by the server. Signed out it simply answers nothing.
 * 2. The lists near the walker, nearest first — only with a usable point.
 * 3. The catalogue in its curated order, which always has rows.
 */
export function defaultPeakListVariables (geo: GeoPoint | null | undefined): SearchPeakListVariables[] {
  const base = { page: 1, perPage: PEAK_LIST_RESULT_LIMIT }
  const nearby = searchPeakListVariables('', geo)
  return [
    { ...base, mineOnly: true },
    ...(nearby.lat !== undefined ? [nearby] : []),
    { ...base, mineOnly: false }
  ]
}

export interface SearchPeakListsResult {
  lists: SearchPeakList[]
  total: number
}

const NO_PEAK_LISTS: SearchPeakListsResult = { lists: [], total: 0 }

/** Runs the GraphQL document with the given variables — graph_client in the app, a fake in tests. */
export type SearchPeakListsRequest = (variables: SearchPeakListVariables) => Promise<any>

/**
 * The rows for the section, or none at all.
 *
 * With nothing typed it is always three rows — recently bagged, then nearby,
 * then the curated catalogue, topped up without repeats — so the section and
 * its tab are there on every open. Nearby alone used to be the whole answer,
 * and outside the areas the lists cover (anywhere in Pakistan, say) the server
 * rightly returned nothing, so the section never showed. Only bagged lists
 * count from the first source: "recently bagged" is its point.
 *
 * Peak bagging's web surface is not public yet: it shows to admins only until
 * PEAK_BAGGING_WEB is switched on (PeakBagging.visible_to?), and the list pages
 * this section links to 404 for everyone else. The peak GraphQL queries answer
 * regardless — shipped app builds depend on them — so the web has to decide for
 * itself, from the flag Rails renders into the page. Hidden means no request,
 * not just no rows, so the feature does not leak through the network tab.
 *
 * A failure is an empty section rather than an error: one section failing
 * must not take the rest of the search down with it.
 */
export async function loadSearchPeakLists (
  query: string,
  geo: GeoPoint | null | undefined,
  request: SearchPeakListsRequest,
  visible: boolean = peakBaggingWebVisible()
): Promise<SearchPeakListsResult> {
  if (!visible) return NO_PEAK_LISTS
  if (!(query || '').trim()) return loadDefaultPeakLists(geo, request)

  try {
    const response = await request(searchPeakListVariables(query, geo))
    const lists: SearchPeakList[] = response?.searchPeakLists?.peakLists || []
    return { lists, total: response?.searchPeakLists?.totalCount || lists.length }
  } catch (error) {
    console.error('Error searching peak lists:', error)
    return NO_PEAK_LISTS
  }
}

/**
 * The empty-box rows: each source in turn until three are filled. A source that
 * fails is skipped rather than emptying the section. `total` is the largest
 * count any source reported, so "View all" still shows when more lists exist.
 */
async function loadDefaultPeakLists (
  geo: GeoPoint | null | undefined,
  request: SearchPeakListsRequest
): Promise<SearchPeakListsResult> {
  const lists: SearchPeakList[] = []
  let total = 0

  for (const variables of defaultPeakListVariables(geo)) {
    if (lists.length >= PEAK_LIST_RESULT_LIMIT) break
    try {
      const response = await request(variables)
      const items: SearchPeakList[] = response?.searchPeakLists?.peakLists || []
      total = Math.max(total, response?.searchPeakLists?.totalCount || 0)
      const usable = variables.mineOnly ? items.filter(list => (list.baggedCount || 0) > 0) : items
      for (const list of usable) {
        if (lists.length >= PEAK_LIST_RESULT_LIMIT) break
        if (!lists.some(shown => shown.id === list.id)) lists.push(list)
      }
    } catch (error) {
      console.error('Error loading default peak lists:', error)
    }
  }

  return { lists, total: Math.max(total, lists.length) }
}

/** What browserLocation needs from `navigator` — injectable so tests need no real browser. */
export interface GeoNavigator {
  permissions?: { query: (descriptor: { name: 'geolocation' }) => Promise<{ state: string }> }
  geolocation?: {
    getCurrentPosition: (
      success: (position: { coords: { latitude: number, longitude: number } }) => void,
      error?: (error: unknown) => void,
      options?: { maximumAge?: number, timeout?: number }
    ) => void
  }
}

/**
 * The browser's own fix — but only when the visitor has already allowed
 * location for the site. Search is not the place to raise the permission
 * prompt, so anything short of "granted" is no fix. Bounded by `timeoutMs`,
 * and any failure is "no fix" rather than an error. Same rule as the apps'
 * SearchLocationFix.
 */
export async function browserLocation (
  timeoutMs = 3000,
  nav: GeoNavigator | undefined = typeof navigator === 'undefined' ? undefined : (navigator as unknown as GeoNavigator)
): Promise<GeoPoint | null> {
  try {
    if (!nav?.permissions?.query || !nav.geolocation) return null
    const status = await nav.permissions.query({ name: 'geolocation' })
    if (status.state !== 'granted') return null

    return await new Promise<GeoPoint | null>((resolve) => {
      const timer = setTimeout(() => resolve(null), timeoutMs)
      nav.geolocation!.getCurrentPosition(
        (position) => {
          clearTimeout(timer)
          resolve({ lat: position.coords.latitude, lng: position.coords.longitude })
        },
        () => {
          clearTimeout(timer)
          resolve(null)
        },
        { maximumAge: 5 * 60 * 1000, timeout: timeoutMs }
      )
    })
  } catch {
    return null
  }
}

/**
 * The point the empty-box "nearby" rows are measured from: the browser's own
 * fix when there is one, otherwise the visitor's IP location. The IP is only
 * city-level, and from Pakistan it always lands far from every list, so the
 * device's own position is preferred wherever the visitor has allowed it.
 */
export async function resolvePeakListLocation (
  ipLocation: GeoPoint | null | undefined,
  browser: () => Promise<GeoPoint | null> = browserLocation
): Promise<GeoPoint | null> {
  return (await browser()) || ipLocation || null
}

